/**
 * Shared inline-SVG generator for sidebet / stake chips.
 *
 * Two size variants:
 *   - 'large': used by the bottom-bar sidebet selector where the chip
 *     is the button face (~80px+). Standard text proportions.
 *   - 'small': used everywhere the chip renders at small DOM size —
 *     profile-card chips, 3D bet-zone owner labels, win-flyout bet
 *     icons (~24-40px). Same circle, much larger fill text so the
 *     label stays readable.
 *
 * Both variants render at viewBox 80x80; the difference is purely the
 * font scale, so the same SVG asset works as a CSS background or img
 * src across browser DPI / zoom levels.
 */
export type SidebetIconSize = 'large' | 'small';

export function sidebetCircleIcon(
  label: string,
  fill: string,
  size: SidebetIconSize = 'large',
): string {
  // Length-aware font scale so multi-char labels never overflow the circle
  // (the r=36 face fits ~64px of condensed Bebas glyphs). Both variants step
  // down as the label gets longer; large text is generous for the short
  // come-out chips ('B', '4+') and still fits the 4-char '4/10'.
  const len = label.length;
  const fontSize = size === 'small'
    ? (len <= 1 ? 60 : len === 2 ? 50 : len === 3 ? 40 : 32)
    : (len <= 1 ? 46 : len === 2 ? 38 : len === 3 ? 34 : 28);
  const stroke = size === 'small' ? 2 : 3;
  // Vertical centring: SVG text y-coord is the baseline. Bebas Neue's
  // cap-height is ~0.7 × fontSize, so for the visual centre to land at
  // the circle centre (y=40) the baseline goes at 40 + 0.35 × fontSize.
  // Old `50 + (fontSize - 28) / 2` was tuned for 22/32 only and pushed
  // the baseline too low at fontSize 60+, hence the off-centre 'B'.
  const baselineY = Math.round(40 + 0.35 * fontSize);
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 80 80'>` +
    `<circle cx='40' cy='40' r='36' fill='${fill}' stroke='#08080A' stroke-width='${stroke}'/>` +
    `<text x='40' y='${baselineY}' font-family='Bebas Neue, Impact, sans-serif' ` +
    `font-weight='900' font-size='${fontSize}' text-anchor='middle' fill='#08080A'>${label}</text>` +
    `</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/**
 * Per-bet-type colour + label registry. Single source of truth for
 * how each sidebet / stake type renders. New bet types are added
 * here once and picked up by both surfaces.
 *
 * Keys MUST match the server-side type strings used in BetZone /
 * SidebetButtonsOverlay options / PlayerProfilesOverlay chips.
 */
export interface SidebetIconSpec {
  /** Multi-char label used in the large (button-bar) variant. */
  label: string;
  /**
   * Optional shorter label used in the small variant only. For wins/
   * loses chips, colour already carries the meaning, so dropping the
   * W/L suffix gives the single remaining glyph more room to fill the
   * chip and stay readable at small render sizes. Falls back to
   * `label` when omitted.
   */
  smallLabel?: string;
  fill: string;
}

export const SIDEBET_ICON_SPECS: Record<string, SidebetIconSpec> = {
  // Cee-lo sidebets — BW/BL theme (Bank Wins / Bank Loses).
  // Small variant drops to just 'B' since green/red already says
  // wins/loses; the lone letter fills the chip for readability.
  bank_wins:    { label: 'BW', smallLabel: 'B', fill: '#00FF88' },
  bank_loses:   { label: 'BL', smallLabel: 'B', fill: '#FF3366' },
  // Large keeps 4+/3- (room for the over/under glyph); small drops to
  // just the digit, which fills the chip at the single-char font scale
  // (gold vs cyan still tells the two apart).
  four_up:      { label: '4+', smallLabel: '4', fill: '#FFD700' },
  three_down:   { label: '3-', smallLabel: '3', fill: '#00F5FF' },

  // Craps come-out sidebets — SW/SL theme (Shooter Wins / Loses).
  // Same single-letter trick for the small variant.
  shooter_wins:  { label: 'SW', smallLabel: 'S', fill: '#00FF88' },
  shooter_loses: { label: 'SL', smallLabel: 'S', fill: '#FF3366' },

  // Craps point-pair sidebets (mid-round, multi-roll). Keys MUST match the
  // server CrapsSideBetType enum values (point_pair_4_10 / 5_9 / 6_8) — the
  // created zone's betType is what getSidebetIconUrl() looks up here.
  // Large keeps the slash; small swaps to a dash — sits on the mid-line and
  // reads cleaner than '/' or '|' at profile-card / zone-label sizes.
  point_pair_4_10: { label: '4/10', smallLabel: '4-10', fill: '#FFD700' },
  point_pair_5_9:  { label: '5/9',  smallLabel: '5-9',  fill: '#00F5FF' },
  point_pair_6_8:  { label: '6/8',  smallLabel: '6-8',  fill: '#FF0080' },

  // Solo Grind — Hi-Lo (Over/Under 7). LO cyan (under), HI gold (over),
  // SEVEN magenta (the lottery bet). Keys match SoloRules bet codes.
  lo:    { label: 'LO', fill: '#00F5FF' },
  hi:    { label: 'HI', fill: '#FFD700' },
  seven: { label: '7',  fill: '#FF0080' },

  // Solo Grind — Chuck-a-Luck. One chip per die face; the digit fills the
  // chip, single violet fill (the number carries the meaning).
  face_1: { label: '1', fill: '#7C5CFF' },
  face_2: { label: '2', fill: '#7C5CFF' },
  face_3: { label: '3', fill: '#7C5CFF' },
  face_4: { label: '4', fill: '#7C5CFF' },
  face_5: { label: '5', fill: '#7C5CFF' },
  face_6: { label: '6', fill: '#7C5CFF' },

  // banker_stake_* removed — the banker / shooter main-stake chip
  // now reuses bank_wins/bank_loses (cee-lo) and shooter_wins/
  // shooter_loses (craps) directly. Owner of a banker zone is rooting
  // for the bank/shooter to WIN; faders are rooting for it to LOSE.
  // The chip carries that meaning automatically.
};

/**
 * Resolve a bet-type to its inline-SVG data URI. Empty string for
 * unknown types. Defaults to large; pass 'small' for any chip surface
 * smaller than ~40px (profile cards, zone labels, win-flyout icons).
 */
export function getSidebetIconUrl(type: string, size: SidebetIconSize = 'large'): string {
  const spec = SIDEBET_ICON_SPECS[type];
  if (!spec) return '';
  const label = size === 'small' && spec.smallLabel ? spec.smallLabel : spec.label;
  return sidebetCircleIcon(label, spec.fill, size);
}
